<template>
  <div>

    <div 
      v-if="!firstLoad" 
      class="estimation__info columns">
      <div class="column flex flex--row flex--align-center">
        <div class="px-3">
          <label class="help">Project</label>
          <span class="subtitle">
            {{ estimation.rfq.project.project_name }}
          </span>
        </div>
        <div class="px-3">
          <label class="help">RFQ</label>
          <span class="subtitle">
            <a :href="estimation.rfq.attachment" target="_blank">
               {{ estimation.rfq.rfq_name }}
            </a>
          </span>
        </div>
        <div class="px-3">
          <label class="help">Estimation Status</label>
          <span class="subtitle">
            {{ estimationStatus }}
          </span>
        </div>
      </div>

      <div 
        class="column flex flex--align-center buttons flex--justify-end">

        <button
          v-if="!firstLoad && !estimation.is_completed"
          @click="confirmMarkComplete()"
          class="button is-primary">
          <span class="icon is-medium">
            <i class="fas fa-check"></i>
          </span>
          <span>              
            Mark as Complete
          </span>
        </button>

        <button
          v-if="!firstLoad && estimation.is_completed && !estimation.is_director_approved"
          @click="confirmDirectorApproval()"
          class="button is-primary">
          <span class="icon is-medium">
            <i class="fas fa-check"></i>
          </span>
          <span>              
            Approved by Director
          </span>
        </button>

        <button
          v-if="
            !firstLoad && 
            estimation.is_director_approved && 
            !estimation.is_sales_approved"
          @click="confirmSalesApproval()"
          class="button is-primary">
          <span class="icon is-medium">
            <i class="fas fa-check"></i>
          </span>
          <span>              
            Approved by Sales
          </span>
        </button>

        <button
          v-if="
            !firstLoad && 
            estimation.is_sales_approved && 
            !estimation.is_client_approved"
          @click="confirmClientApproval()"
          class="button is-primary">
          <span class="icon is-medium">
            <i class="fas fa-check"></i>
          </span>
          <span>              
            Approved by Client
          </span>
        </button>

        <button 
          v-if="!firstLoad && !estimation.is_completed"
          class="button is-link" 
          :class="{ 'is-loading': saving }"
          :disabled="saving"
          @click.stop="saveChanges()">
          <span class="icon is-medium">
            <i class="fas fa-save"></i>
          </span>
          <span>              
            Save and Refresh
          </span>
        </button>

        <button
          @click="adjustingParameters = true" 
          class="button">
          <span class="icon is-small">
            <i class="fas fa-cogs"></i>
          </span>
        </button>
      </div>           
    </div>

    <div class="tabs mb-3 mt-2">
      <ul>
        <li 
          @click="active = 'wall_details'"
          :class="{ 'is-active': active == 'wall_details' }">
          <a>Wall Data</a>
        </li>
        <li 
          @click="active = 'rail_details'"
          :class="{ 'is-active': active == 'rail_details' }">
          <a>Railing Data</a>
        </li>        
        <li 
          v-if="!firstLoad && estimation.rfq.drawing_highlights &&
            estimation.rfq.drawing_highlights.length > 0 && 
            estimation.rfq.drawing_highlights[0].is_approved"
          @click="active = 'drawing_highlights'"
          :class="{ 'is-active': active == 'drawing_highlights' }">
          <a>Drawing Highlights</a>
        </li>
        <li 
          @click="active = 'summary'"
          :class="{ 'is-active': active == 'summary' }">
          <a>Summary</a>
        </li>
        <li 
          @click="active = 'environmental_impact'"
          :class="{ 'is-active': active == 'environmental_impact' }">
          <a>Environmental Impact</a>
        </li>
        <template v-if="!summaryFirstLoad">
          <li 
            v-for="summaryTab in summaryTabs"
            :key="summaryTab"
            class="capitalize"
            :class="{ 'is-active': active == summaryTab }"
            @click="active = summaryTab">
            <a>{{ summaryTab.replaceAll("_", " ") }}</a>
          </li>
        </template>

      </ul>
    </div>

    <div
      v-show="active == 'wall_details'" 
      class="estimation__details pb-3">
      <div id="wall-spreadsheet">
      </div>
    </div>

    <div
      v-show="active == 'rail_details'" 
      class="estimation__details pb-3">
      <div id="rail-spreadsheet">
      </div>
    </div>

    <div
      v-if="active == 'drawing_highlights'" 
      class="estimation__details pb-3">
      <drawing-highlights 
        :drawing-highlight="estimation.rfq.drawing_highlights[0]"/>
    </div>

    <div 
      v-if="active=='summary' && !summaryFirstLoad && !firstLoad"
      class="px-3 py-2">

      <template 
        v-if="Object.keys(estimationSummary).includes('by_material') && 
          estimationSummary['by_material'].length > 0">
        <div 
          class="is-size-6 mb-2 has-text-weight-bold">
          Summary of Price by Material
        </div>

        <table
          class="table is-fullwidth is-hoverable has-small-font mb-0 summary-table">
          <thead>
            <tr>
              <template 
                v-for="(value, label) in estimationSummary['by_material'][0]">
                <th  
                  class="capitalize"
                  :class="columnClasses[label]"
                  v-if="label != 'material_id'"
                  :key="label">
                  {{ label.replaceAll("_", " ") }}
                </th>
              </template>
            </tr>
          </thead>
          <tbody>
            <tr 
              class="v-centered"
              v-for="(row, i) in estimationSummary['by_material']"
              :key="i">
              <template
                v-for="(rowValue, rowLabel) in row">
                <td 
                  :class="columnClasses[rowLabel]"
                  v-if="!['material_id', 'unit_price_adjustment'].includes(rowLabel)"
                  :key="rowLabel">
                  {{ formatValue(rowValue) }}
                </td>
                <td 
                  :class="columnClasses[rowLabel]"
                  v-if="rowLabel == 'unit_price_adjustment'"
                  :key="rowLabel">
                  <div class="flex flex--row flex--align-center">
                    <v-text-field
                      class="flex--grow"
                      required
                      :value="rowValue"
                      @input="updateSummary('by_material', i, 'unit_price_adjustment', $event)"
                      :disabled="estimation.is_completed"
                      :rules="[
                        v => typeof parseFloat(v) === 'number' || 'Please enter a number'
                      ]"/>
                    <button 
                      class="button is-primary is-small ml-2"
                      @click="adjustPrice('by_material', row, row.unit_price_adjustment)">
                      <span 
                        class="icon is-small" 
                        title="save changes">
                        <i class="fas fa-save">
                        </i>
                      </span>
                    </button>
                  </div>
                </td>
              </template>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <template 
                v-for="(value, label) in estimationSummary['by_material'][0]">
                <th  
                  class="capitalize"
                  :class="columnClasses[label]"
                  v-if="label != 'material_id'"
                  :key="label">
                  {{ getTotal(label, estimationSummary['by_material'], 'summary') }}
                </th>
              </template>
            </tr>
          </tfoot>
        </table>

        <hr/>
      </template>

      <template 
        v-if="Object.keys(estimationSummary).includes('irregular_price_by_material') && 
          estimationSummary['irregular_price_by_material'].length > 0">
        <div 
          class="is-size-6 mb-2 has-text-weight-bold">
          Summary of Price by Irregular Material
        </div>

        <table
          class="table is-fullwidth is-hoverable has-small-font mb-0 summary-table">
          <thead>
            <tr>
              <template 
                v-for="(value, label) in estimationSummary['irregular_price_by_material'][0]">
                <th  
                  class="capitalize"
                  :class="columnClasses[label]"
                  v-if="label != 'material_id'"
                  :key="label">
                  {{ label.replaceAll("_", " ") }}
                </th>
              </template>
            </tr>
          </thead>
          <tbody>
            <tr 
              class="v-centered"
              v-for="(row, i) in estimationSummary['irregular_price_by_material']"
              :key="i">
              <template
                v-for="(rowValue, rowLabel) in row">
                <td 
                  :class="columnClasses[rowLabel]"
                  v-if="!['material_id', 'unit_price_adjustment'].includes(rowLabel)"
                  :key="rowLabel">
                  {{ formatValue(rowValue) }}
                </td>
                <td 
                  :class="columnClasses[rowLabel]"
                  v-if="rowLabel == 'unit_price_adjustment'"
                  :key="rowLabel">
                  <div class="flex flex--row flex--align-center">
                    <v-text-field
                      class="flex--grow"
                      required
                      :value="rowValue"
                      @input="updateSummary(
                        'irregular_price_by_material', i, 'unit_price_adjustment', $event
                      )"
                      :disabled="estimation.is_completed"
                      :rules="[
                        v => typeof parseFloat(v) === 'number' || 'Please enter a number'
                      ]"/>
                    <button 
                      class="button is-primary is-small ml-2"
                      @click="adjustPrice(
                        'irregular_cladding', row, row.unit_price_adjustment
                      )">
                      <span 
                        class="icon is-small" 
                        title="save changes">
                        <i class="fas fa-save">
                        </i>
                      </span>
                    </button>
                  </div>
                </td>
              </template>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <template 
                v-for="(value, label) in estimationSummary['irregular_price_by_material'][0]">
                <th  
                  class="capitalize"
                  :class="columnClasses[label]"
                  v-if="label != 'material_id'"
                  :key="label">
                  {{ getTotal(label, estimationSummary['irregular_price_by_material'], 'summary') }}
                </th>
              </template>
            </tr>
          </tfoot>
        </table>

        <hr/>
      </template>

      <template 
        v-if="estimationSummary['summary'].length > 0">
        <div  
          class="is-size-6 mb-2 has-text-weight-bold">
          Pricing Summary
        </div>

        <table
          class="table is-fullwidth is-hoverable has-small-font mb-0 summary-table">
          <thead>
            <tr>
              <template 
                v-for="(value, label) in sortedSummary[0]">
                <th  
                  class="capitalize"
                  :class="columnClasses[label]"
                  v-if="label != 'material_id'"
                  :key="label">
                  {{ label.replaceAll("_", " ") }}
                </th>
              </template>
            </tr>
          </thead>
          <tbody>
            <template v-for="(row, i) in sortedSummary">
              <tr 
                v-if="summaryRowsOrder.includes(row.pricing_summary)"
                class="v-centered"
                :key="i">
                <template
                  v-for="(rowValue, rowLabel) in row">
                  <td 
                    :class="columnClasses[rowLabel]"
                    :key="rowLabel">
                    {{ formatValue(rowValue) }}
                  </td>
                </template>
              </tr>
            </template>
            <tr class="subtotal-row">
              <td class="has-text-weight-bold">
                Subtotal
              </td>
              <template 
                v-for="(value, label) in sortedSummary[0]">
                <th  
                  :class="columnClasses[label]"
                  v-if="label != 'pricing_summary'"
                  :key="label">
                  {{ getTotal(
                      label, 
                      sortedSummary.filter(
                        d => summaryRowsOrder.includes(d.pricing_summary)
                      ),
                      'summary',
                      true
                    ) }}
                </th>
              </template>
            </tr>
            <tr v-if="railingSummary">
              <td 
                v-for="(rowValue, rowLabel) in railingSummary"
                :class="columnClasses[rowLabel]"
                :key="rowLabel">
                {{ formatValue(rowValue) }}
              </td>
            </tr>
            <tr v-if="railingSummary" class="subtotal-row">
              <td class="has-text-weight-bold">
                Subtotal
              </td>
              <template 
                v-for="(value, label) in railingSummary">
                <th  
                  :class="columnClasses[label]"
                  v-if="label != 'pricing_summary'"
                  :key="label + '-subtotal'">
                  {{ getTotal(
                      label, 
                      estimationSummary['summary'].filter(
                        d => summaryRowsOrder.includes(d.pricing_summary) || 
                        d.pricing_summary.includes('railing')
                      ),
                      'summary',
                      true
                    ) }}
                </th>
              </template>
            </tr>
            <tr v-if="batterySummary" class="v-centered">
              <template v-for="(rowValue, rowLabel) in batterySummary">
                <td 
                  v-if="!['system_size', 'average_value', 'average_actual_price'].includes(rowLabel)"
                  :class="columnClasses[rowLabel]"
                  :key="rowLabel">
                  {{ formatValue(rowValue) }}
                </td>
                <td 
                  v-if="rowLabel == 'average_actual_price'"
                  :class="columnClasses[rowLabel]"
                  :key="rowLabel">
                  <v-text-field
                    type="number" 
                    v-model="estimation.battery_unit_price"
                    @blur="update('battery_unit_price', $event, true)"/>
                </td>
                <td 
                  v-if="rowLabel == 'system_size'"
                  :class="columnClasses[rowLabel]"
                  :key="rowLabel">
                  <v-text-field
                    type="number" 
                    v-model="estimation.batteries_system_size_kw"
                    @blur="update('batteries_system_size_kw', $event, true)"/>
                </td>
                <td 
                  v-if="rowLabel == 'average_value'"
                  :class="columnClasses[rowLabel]"
                  :key="rowLabel">
                  <v-text-field
                    type="number" 
                    v-model="estimation.batteries_and_invertors_val"
                    @blur="update('batteries_and_invertors_val', $event, true)"/>
                </td>
              </template>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <template 
                v-for="(value, label) in estimationSummary['summary'][0]">
                <th  
                  class="capitalize"
                  :class="columnClasses[label]"
                  v-if="label != 'material_id'"
                  :key="label">
                  {{ getTotal(label, estimationSummary['summary'], 'summary') }}
                </th>
              </template>
            </tr>
          </tfoot>
        </table>
        
      </template>

      <hr/>

      <div class="columns is-multiline">
        <template v-for="table in summaryTables">
          <div 
            v-if="estimationSummary[table.section] && 
              estimationSummary[table.section].length > 0"
            :key="table.section"
            :class="table.class">
            <div class="is-size-6 mb-2 has-text-weight-bold">
              {{ table.title }}
            </div>
            <table
              class="table is-hoverable has-small-font is-fullwidth mb-0 summary-table">
              <thead>
                <tr>
                  <template 
                    v-for="(value, label) in estimationSummary[table.section][0]">
                    <th  
                      class="capitalize"
                      :class="columnClasses[label]"
                      v-if="label != 'material_id'"
                      :key="label">
                      {{ label.replaceAll("_", " ") }}
                    </th>
                  </template>
                </tr>
              </thead>
              <tbody>
                <tr 
                  class="v-centered"
                  v-for="(row, i) in estimationSummary[table.section]"
                  :key="i">
                  <template
                    v-for="(rowValue, rowLabel) in row">
                    <td 
                      :class="columnClasses[rowLabel]"
                      v-if="!['material_id', 'unit_price_adj'].includes(rowLabel)"
                      :key="rowLabel">
                      {{ formatValue(rowValue) }}
                    </td>
                  </template>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <template 
                    v-for="(value, label) in estimationSummary[table.section][0]">
                    <th  
                      class="capitalize"
                      :class="columnClasses[label]"
                      v-if="label != 'material_id'"
                      :key="label">
                      {{ getTotal(label, estimationSummary[table.section], 'summary') }}
                    </th>
                  </template>
                </tr>
              </tfoot>
            </table>
          </div>
        </template>
      </div>

    </div>

    <div 
      v-if="active=='environmental_impact'"
      class="px-3 py-2">      
      <div class="is-size-6 mb-1 has-text-weight-bold">
        Greenhouse Gas Equivalencies
      </div>
      <div class="columns">
        <template v-for="ratio in ghgEquiv">
          <div class="column" :key="ratio">
            <label class="help capitalize mb-1 ml-2">
              {{ ratio.replaceAll("_", " ") }}
            </label>
            <v-text-field
              type="number" 
              v-model="estimation[ratio]"
              @blur="update(ratio, $event)"/>
          </div>
        </template>
      </div>

      <hr class="my-3"/>

      <div class="is-size-6 mb-1 has-text-weight-bold">
        Sun Hours
      </div>
      <div class="columns">
        <template v-for="elevation in sunHourFields">
          <div class="column" :key="elevation">
            <label class="help capitalize mb-1 ml-2">
              {{ elevation.replaceAll("_", " ") }}
            </label>
            <v-text-field
              type="number" 
              v-model="estimation[elevation]"
              @blur="update(elevation, $event)"/>
          </div>
        </template>
      </div>

      <hr class="my-3"/>

      <div class="is-size-6 mb-1 has-text-weight-bold">
        Environmental Impact Output
      </div>
      <table class="table is-fullwidth">
        <thead>
          <tr>
            <th>Years</th>
            <th class="has-text-right">Energy (kWh)</th>
            <th class="has-text-right">Cars Per Year</th>
            <th class="has-text-right">Metric Tonnes of CO2</th>
            <th class="has-text-right">Electricity of Homes Per Year</th>
            <th class="has-text-right">Trees Planted</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>1</td>
            <td class="has-text-right">
              {{ totEnergy | commasep(0) }}
            </td>
            <td class="has-text-right">
              {{ totEnergy / 1000 * estimation.cars_per_year_ratio | commasep(0) }}
            </td>
            <td class="has-text-right">
              {{ totEnergy / 1000 * estimation.metric_tones_co2_ratio | commasep(0) }}
            </td>
            <td class="has-text-right">
              {{ totEnergy / 1000 * estimation.elec_homes_ratio | commasep(0) }}
            </td>
            <td class="has-text-right">
              {{ totEnergy / 1000 * estimation.no_of_trees_ratio | commasep(0) }}
            </td>
          </tr>
          <tr>
            <td>30</td>
            <td class="has-text-right">
              {{ totEnergy * 30 | commasep(0)}}
            </td>
            <td class="has-text-right">
              {{ totEnergy / 1000 * estimation.cars_per_year_ratio * 30 | commasep(0) }}
            </td>
            <td class="has-text-right">
              {{ totEnergy / 1000 * estimation.metric_tones_co2_ratio * 30 | commasep(0) }}
            </td>
            <td class="has-text-right">
              {{ totEnergy / 1000 * estimation.elec_homes_ratio * 30 | commasep(0) }}
            </td>
            <td class="has-text-right">
              {{ totEnergy / 1000 * estimation.no_of_trees_ratio * 30 | commasep(0) }}
            </td>
          </tr>
        </tbody>
      </table>

    </div>

    <template 
      v-if="!summaryFirstLoad && !firstLoad">
      <template 
        v-for="section in summaryTabs">
        <div 
          v-if="section != 'summary'"
          :key="`${section}-data`"
          class="px-3">
          <table
            v-show="section == active"
            class="table is-fullwidth is-hoverable has-small-font summary-table">
            <thead>
              <tr>
                <template 
                  v-for="(value, label) in estimationSummary[section][0]">
                  <th  
                    class="capitalize"
                    :class="columnClasses[label]"
                    v-if="label != 'material_id'"
                    :key="label">
                    {{ label.replaceAll("_", " ") }}
                  </th>
                </template>
              </tr>
            </thead>
            <tbody>
              <tr 
                class="v-centered"
                v-for="(row, i) in estimationSummary[section]"
                :key="i">
                <template
                  v-for="(rowValue, rowLabel) in row">
                  <td 
                    v-if="!['material_id'].includes(rowLabel)"
                    :class="columnClasses[rowLabel]"
                    :key="rowLabel">
                    {{ formatValue(rowValue) }}
                  </td>
                </template>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <template 
                  v-for="(value, label) in estimationSummary[section][0]">
                  <th  
                    class="capitalize"
                    :class="columnClasses[label]"
                    v-if="label != 'material_id'"
                    :key="label">
                    {{ getTotal(label, estimationSummary[section], section) }}
                  </th>
                </template>
              </tr>
            </tfoot>
          </table>
        </div>

      </template>
    </template>

    <v-confirm-modal
      v-model="confirmModal.state"
      :title="confirmModal.title"
      :type="confirmModal.type"
      :message="confirmModal.message"
      @confirm="confirmModal.confirmCb"/>

    <adjust-parameters-modal 
      v-if="!firstLoad"
      :estimation="estimation"
      :show-params="showParams"
      v-model="adjustingParameters"
      @save-changes="saveEstimationParams"/>

  </div>
</template>

<script>
import VConfirmModal from '@/components/base/VConfirmModal'
import VTextField from '@/components/base/VTextField'
import AdjustParametersModal from '@/components/estimations/AdjustParametersModal'
import DrawingHighlights from '@/components/estimations/DrawingHighlights'

import { estimationHelpers, estimationStatus } from '@/mixins/helpers'

import jexcel from 'jexcel'
import 'jexcel/dist/jexcel.css'

import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  components: { 
    VConfirmModal, AdjustParametersModal, 
    VTextField, DrawingHighlights
  },
  mixins: [estimationHelpers, estimationStatus],
  data() {
    return {
      active: 'wall_details',
      detailWidth: '500',
      wallFields: [
        'id', 'elevation', 'product_brand', 'material_id',
        'details', 'length_in', 'width_in', 'piece_qty', 
        'area_sqft', 'cell_q', 'power', 'linear_type', 'linear_in_str'
      ],
      railFields: [
        'id', 'elevation', 'material_id', 'details', 'length_in', 'width_in',
        'piece_qty', 'area_sqft', 'solar_panels', 'power', 'linear_in_str'
      ],
      adjustingParameters: false,
      confirmModal: {
        state: false,
        title: 'Confirm mark as complete',
        message: 'Are you sure you want to mark this estimation as complete? \
        Once marked as complete, you will not be able make more edits \
        unless a revision is requested ',
        type: 'warning',
        confirmCb: () => {}
      },
      originalIds: [],
      saving: false,
      summaryTables: [
        { 
          section: 'price_by_material_cladding', 
          title: 'Summary of Price by Material and Product Brand', 
          class: 'column is-12' 
        },
        { 
          section: 'by_cladding_elevation', 
          title: 'Cladding Summary of Size by Elevation', 
          class: 'column is-12' 
        },
        { 
          section: 'by_railing_elevation', 
          title: 'Railing Summary of Size by Elevation', 
          class: 'column is-12'
        }
      ],
      summaryTabsOrder: [
        'solar_wall_active', 'solar_wall_non_active', 
        'solar_railing_1100_1400', 'cladding', 'irregular_cladding'
      ],
      sunHourFields: [
        'sunlight_e', 'sunlight_n', 'sunlight_ne', 'sunlight_nw',
        'sunlight_s', 'sunlight_se', 'sunlight_sw', 'sunlight_w'
      ],
      ghgEquiv: [
        'cars_per_year_ratio', 'metric_tones_co2_ratio',
        'elec_homes_ratio', 'no_of_trees_ratio'
      ],
      elevationMap: {
        'North': 'sunlight_n', 
        'South': 'sunlight_s',
        'East': 'sunlight_e',
        'West': 'sunlight_w',
        'N/ East': 'sunlight_ne',
        'N/ West': 'sunlight_nw', 
        'S/ East': 'sunlight_se', 
        'S/ West': 'sunlight_sw'
      },
      columnClasses: {
        'sum_area_sqft': 'border--after',
        'total_cost': 'border--after',
        'unit_price_adjustment': 'border--before border--after',
        'unit_price_adj': 'border--before border--after',
        'elec_proceeds_for_mitrex': 'border--before',
        'cladify_area_sqft': 'border--after',
        'cladify_cost': 'border--after',
        'power': 'border--before border--after',
        'system_size': 'border--before border--after',
        'system_capacity': 'border--before border--after',
        'annual_sun_hours': 'border--before',
        'railing_cost': 'border--before'
      },
      summaryRowsOrder: [
        'solar_wall_active', 'solar_wall_non_active', 
        'cladding', 'irregular'
      ],
      batteryMap: {
        'system_size': 'batteries_system_size_kw',
        'average_value': 'batteries_and_invertors_val'
      },
      showParams: [
        "cell_length",
        "cell_width",
        "cell_distance",
        "string_distance",
        "side_margin",
        "top_margin",
        "bottom_margin",
        "top_busbar_space",
        "bottom_busbar_space",
        "busbar_thickness",
        "open_circuit_voltage",
        "solar_capacity_w",
        "solar_wall_active_val",
        "solar_wall_nonactive_val",
        "cladding_val",
        "railing_1100_1400_val",
        "railing_1200_1200_val",
        "irregular_val",
        "batteries_and_invertors_val",
        "batteries_system_size_kw",
        "elec_for_mitrex",
        "elec_for_owner"
      ]
    }
  },
  computed: {
    ...mapState("materials", ["materials"]),
    ...mapState("estimation", [
      "estimation", "loading", "firstLoad",
      "estimationSummary", "summaryLoading", "summaryFirstLoad"
    ]),
    sortedSummary({ estimationSummary, summaryRowsOrder }) {
      return estimationSummary.summary.sort((a,b) => {
        return summaryRowsOrder.indexOf(
          a.pricing_summary
        ) - summaryRowsOrder.indexOf(
          b.pricing_summary
        )
      })
    },
    railingSummary({ estimationSummary }) {
      return estimationSummary.summary.filter(
        d => d.pricing_summary.includes('railing')
      )[0]
    },
    batterySummary({ estimationSummary }) {
      return estimationSummary.summary.filter(
        d => d.pricing_summary.includes('batteries')
      )[0]
    },
    summaryTabs({ estimationSummary, summaryTabsOrder }) {
      let summaryKeys = Object.keys(estimationSummary);
      return summaryTabsOrder.filter(tab => 
        summaryKeys.includes(tab) && estimationSummary[tab].length > 0
      )
    },
    totEnergy({ estimation, estimationSummary, elevationMap, sunHourFields }) {
      return estimationSummary.by_elevation.map(
        d => {
          if (d.elevation == 'Whole Building') {
            let avg = sunHourFields.map(
              elevation => estimation[elevation]
            ).reduce((a,b) => a + +b, 0) / sunHourFields.length

            return avg * d.system_capacity
          } else {
            return d.system_capacity * estimation[elevationMap[d.elevation]]
          }
        }
      ).reduce((a,b) => a + +b, 0)
    },
    wallDetails({ estimation, wallFields }) {
      if (estimation.estimation_details.length > 0) {
        let wall = estimation.estimation_details.filter(d => {
          return d.wall_railing == 'wall'
        }).map(d => {
          return wallFields.map(field => d[field]);
        });

        return wall.length > 0 ? wall : [[]];
      } else {
        return [[]];
      }
    },
    railDetails({ estimation, railFields }) {
      if (estimation.estimation_details.length > 0) {
        let rail = estimation.estimation_details.filter(d => {
          return d.wall_railing == 'railing'
        }).map(d => {
          return railFields.map(field => d[field]);
        });

        return rail.length > 0 ? rail : [[null,null,null,null,null,43.2,0,0,0,0]];
      } else {
        return [[null,null,null,null,null,43.2,0,0,0,0]];
      }
    },
    wallOptions({ detailWidth, wallTotCols }) {
      let cellWidth = detailWidth / (wallTotCols - 1);

      return {
        data: this.wallDetails,
        allowInsertColumn: false,
        allowManualInsertColumn: false,
        allowDeleteColumn: false,
        columnResize: false,
        columns: [
          { type: 'text', width: `${cellWidth}px`, title: 'Id' },
          { 
            type: 'dropdown', 
            width: `${cellWidth}px`, 
            title: 'Elevation', 
            source: this.elevations 
          },
          { 
            type: 'dropdown', 
            width: `${cellWidth}px`, 
            title: 'Product Brand', 
            source: ['Mitrex', 'Cladify']
          },
          { 
            type: 'dropdown', 
            width: `${cellWidth}px`, 
            title: 'Material', 
            source: this.materials,
            // filter: this.wallMaterialFilter,
            autocomplete: true
          },
          { type: 'text', width: `${cellWidth}px`, title: 'Details' },
          { type: 'text', width: `${cellWidth}px`, title: 'L"' },
          { type: 'text', width: `${cellWidth}px`, title: 'W"' },
          { type: 'numeric', width: `${cellWidth}px`, title: 'Quantity (Pieces)' },
          { type: 'number', width: `${cellWidth}px`, title: 'Area (sqft)' },
          { type: 'number', width: `${cellWidth}px`, title: 'Cell Q' },
          { type: 'number', width: `${cellWidth}px`, title: 'Power' },
          { 
            type: 'dropdown', 
            width: `${cellWidth}px`, 
            title: 'Linear Type', 
            source: ['opening', 'pre-assemble'] 
          },
          { type: 'numeric', width: `${cellWidth}px`, title: 'Linear Inch' }
        ],
        oninsertrow: () => { this.wallSpreadsheet.hideColumn(0) },
        onchange: this.wallChanged,
        updateTable: (el, cell, x) => {
          const { wallFields } = this;
          const sqftInd = wallFields.indexOf('area_sqft'),
                cellInd = wallFields.indexOf('cell_q'),
                powerInd = wallFields.indexOf('power');

          if (this.estimation.is_completed) {
            cell.classList.add('readonly');
          } else if (
            [sqftInd, cellInd, powerInd].includes(+x)
          ) {
            cell.classList.add('readonly');
          }
        } 
      }
    },
    railOptions({ detailWidth, railTotCols }) {
      let cellWidth = detailWidth / (railTotCols - 1);

      return {
        data: this.railDetails,
        allowInsertColumn: false,
        allowManualInsertColumn: false,
        allowDeleteColumn: false,
        columnResize: false,
        columns: [
          { type: 'text', width: `${cellWidth}px`, title: 'Id' },
          { 
            type: 'dropdown', 
            width: `${cellWidth}px`, 
            title: 'Elevation', 
            source: this.elevations 
          },
          { 
            type: 'dropdown', 
            width: `${cellWidth}px`, 
            title: 'Material', 
            source: this.materials.filter(d => d.product_brand == 'Mitrex'),
            autocomplete: true,
          },
          { type: 'text', width: `${cellWidth}px`, title: 'Details' },
          { type: 'text', width: `${cellWidth}px`, title: 'L"' },
          { type: 'text', width: `${cellWidth}px`, title: 'W"' },
          { type: 'numeric', width: `${cellWidth}px`, title: 'Quantity (Pieces)' },
          { type: 'number', width: `${cellWidth}px`, title: 'Area (sqft)' },
          { type: 'number', width: `${cellWidth}px`, title: 'Solar Panels' },
          { type: 'number', width: `${cellWidth}px`, title: 'Power' },
          { type: 'numeric', width: `${cellWidth}px`, title: 'Linear Inch' }
        ],
        oninsertrow: (instance, rowNumber) => { 
          this.railSpreadsheet.hideColumn(0);
          this.railInsertRow(instance, rowNumber);
        },
        onchange: this.railChanged,
        updateTable: (el, cell, x) => {
          const { railFields } = this;

          const sqftInd = railFields.indexOf('area_sqft'),
                panelInd = railFields.indexOf('solar_panels'),
                lfInd = railFields.indexOf('linear_in_str'),
                powerInd = railFields.indexOf('power');

          if (this.estimation.is_completed) {
            cell.classList.add('readonly');
          } else if (
            [sqftInd, panelInd, lfInd, powerInd].includes(+x)
          ) {
            cell.classList.add('readonly');
          }
        } 
      }
    }
  },
  mounted() {
    this.initialize();
  },
  methods: {
    ...mapActions("materials", ["getMaterials"]),
    ...mapActions("estimation", [
      "getEstimationSummary","getEstimation", "updateEstimation", 
      "addDetail", "updateDetail", "deleteDetail", "adjustSummaryPrice"
    ]),
    ...mapMutations("estimation", [
      "setEstimation", "setLoading", "setFirstLoad"
    ]),
    ...mapMutations("app", ["setNotification"]),
    initialize() {
      if (this.$route.params.rfq) {
        this.getEstimationSummary(this.$route.params.estimationId);
        let rfq = this.$route.params.rfq;
        rfq.estimation.rfq = this.$route.params.rfq;
        this.setEstimation(rfq.estimation);
        this.originalIds = [...this.estimation.estimation_details.map(d => d.id)];
        this.setLoading(false);
        this.setFirstLoad(false);
        this.checkMaterials();
      } else {
        this.getEstimationSummary(this.$route.params.estimationId);
        this.getEstimation(this.$route.params.estimationId).then(() => {
          this.originalIds = [...this.estimation.estimation_details.map(d => d.id)];
          this.checkMaterials();
        })
      }
    },
    initializeSpreadsheet() {
      this.setWidth();

      // Initialize railing spreadsheet
      let wallSpreadsheet = jexcel(
        document.getElementById("wall-spreadsheet"), this.wallOptions
      );
      Object.assign(this, { wallSpreadsheet });
      wallSpreadsheet.hideColumn(0);

      // Initialize railing spreadsheet
      let railSpreadsheet = jexcel(
        document.getElementById("rail-spreadsheet"), this.railOptions
      );
      Object.assign(this, { railSpreadsheet });
      railSpreadsheet.hideColumn(0);
    },
    update(field, value, refreshSummary=false) {
      let payload = {}
      payload[field] = value;

      this.updateEstimation({
        estimationId: this.estimation.id,
        data: payload
      }).then(() => {
        if (refreshSummary) {
          this.getEstimationSummary(this.estimation.id);
        }
      })
    },
    updateSummary(summaryTable, rowIndex, property, v) {
      this.estimationSummary[summaryTable][rowIndex][property] = v;
    },
    formatValue(v) {
      if (isNaN(v)) {
        return v.replaceAll("_", " ");
      } else {
        return v ? this.$options.filters.commasep(v, 0) : 0;
      }
    },
    getTotal(label, summary, section, isSubtotal) {
      let values = summary.map(d => d[label]);
      if (
        isNaN(values[0]) || 
        ['unit_price_adj', 
        'unit_price_adjustment', 
        'annual_sun_hours'].includes(label)
      ) {
        return '';
      } else if (
        ['unit_price', 'adjusted_unit_price'].includes(label)
      ) {
        let sum = values.reduce((a, b) => a + +b, 0);
        let length = summary.map(d => d[label]).length;

        return this.$options.filters.commasep(sum / length, 0);
      } else if ([
        'average_actual_price', 'average_value', 'system_size'
      ].includes(label) && !isSubtotal) {
        let sum = this.estimationSummary[section].filter(d => 
          !d.pricing_summary.includes('batteries')
        ).map(d => d[label]).reduce((a, b) => a + +b, 0);

        return this.$options.filters.commasep(sum, 0);
      } else {
        let sum = values.reduce((a, b) => a + +b, 0);
        return this.$options.filters.commasep(sum, 0);
      }
    },
    confirmMarkComplete() {
      this.confirmModal.confirmCb = () => {
        this.updateEstimation({
          estimationId: this.estimation.id,
          data: { 'is_completed': true }
        }).then(() => {
          this.confirmModal.state = false;
          this.wallSpreadsheet.updateTable(
            function (el, cell, x) { 
              cell.classList.add('readonly');
            }
          )
          this.railSpreadsheet.updateTable(
            function (el, cell, x) { 
              cell.classList.add('readonly'); 
            }
          )
        })
      }
      this.confirmModal.state = true;
    },
    confirmDirectorApproval() {
      this.confirmModal.confirmCb = () => {
        this.updateEstimation({
          estimationId: this.estimation.id,
          data: { 'is_director_approved': true }
        }).then(() => {
          this.confirmModal.state = false;
        })
      }
      this.confirmModal.title = "Confirm Director Approval"
      this.confirmModal.message = "Are you sure you want to approve this estimation?"
      this.confirmModal.state = true;
    },
    confirmSalesApproval() {
      this.confirmModal.confirmCb = () => {
        this.updateEstimation({
          estimationId: this.estimation.id,
          data: { 'is_sales_approved': true }
        }).then(() => {
          this.confirmModal.state = false;
        })
      }
      this.confirmModal.title = "Confirm Sales Approval"
      this.confirmModal.message = "Are you sure you want to approve this estimation?"
      this.confirmModal.state = true;
    },
    confirmClientApproval() {
      this.confirmModal.confirmCb = () => {
        this.saveChanges().then(() => {
          this.updateEstimation({
            estimationId: this.estimation.id,
            data: { 'is_client_approved': true }
          }).then(() => {
            this.confirmModal.state = false;
          })
        })
      }
      this.confirmModal.title = "Confirm Client Approval"
      this.confirmModal.message = "Are you sure you want to mark this estimation as \
      approved by the client? No further revisions can be made after client approval. \
      Note: Any unsaved changes will be automatically saved."
      this.confirmModal.state = true;
    },
    adjustPrice(section, row, v) {
      let payload = {
        summary_table: section,
        material_id: row.material_id,
        adjustment_unit_price_val: v
      }

      this.adjustSummaryPrice({
        estimationId: this.estimation.id,
        data: payload
      })
    },
    adjustBatteryPrice(v) {
      this.adjustSummaryPrice({
        estimationId: this.estimation.id,
        data: {
          summary_table: 'summary',
          pricing_summary: 'batteries and invertors',
          price: v
        }
      })
    },
    saveEstimationParams() {
      let data = {};
      this.showParams.forEach(param => {
        data[param] = this.estimation[param]
      })
      this.updateEstimation({
        estimationId: this.estimation.id,
        data: data
      }).then(() => {
        this.adjustingParameters = false;
        this.setNotification({
          status: true,
          type: 'success',
          message: 'Estimation successfully updated'
        });
        this.getEstimationSummary(this.estimation.id);
      })
    },
    saveChanges() {
      this.saving = true;

      //////////////////////////////////
      // Clean up data
      //////////////////////////////////
      const {wallFields} = this;
      let wall = this.wallSpreadsheet.getData().map(d => {
        return Object.fromEntries(
          wallFields.map((_, i) => {
            let v = d[i] == "" ? null : d[i].toString() //Don't send blank, send null instead
            return [wallFields[i], v]
          })
        )
      }).filter(d => d.material_id != null && d.piece_qty != null)

      const {railFields} = this;
      let railing = this.railSpreadsheet.getData().map(d => {
        return Object.fromEntries(
          railFields.map((_, i) => {
            let v = d[i] == "" ? null : d[i].toString() //Don't send blank, send null instead
            return [railFields[i], v]
          })
        )
      }).filter(d => d.material_id != null && d.piece_qty != null);

      //////////////////////////////////
      // Add the order
      //////////////////////////////////
      wall.forEach((row, i) => {
        row.order = i;
      });

      railing.forEach((row, i) => {
        row.order = i;
      });

      let promises = [];

      //////////////////////////////////
      // Delete
      //////////////////////////////////
      let newIds = [...wall.map(d => +d.id).concat(railing.map(d => +d.id))];
      let deleteIds = this.originalIds.filter(id => !newIds.includes(+id));
      deleteIds.forEach(deleteId => {
        promises.push(this.deleteDetail(deleteId));
      })

      //////////////////////////////////
      // Add
      //////////////////////////////////
      let newWallDetails = wall.filter(d => !d.id).map(d => {
        d.estimation_id = this.estimation.id;
        d.wall_railing = "wall";
        // delete d.area_sqft;
        // delete d.cell_q;
        delete d.id;
        return d
      })
      let newRailDetails = railing.filter(d => !d.id).map(d => {
        d.estimation_id = this.estimation.id;
        d.wall_railing = "railing";
        d.product_brand = "Mitrex"
        // delete d.area_sqft;
        // delete d.linear_in_str;
        // delete d.solar_panels;
        delete d.id;
        return d
      })

      newWallDetails.concat(newRailDetails).forEach(detail => {
        promises.push(this.addDetail(detail));
      })

      
      //////////////////////////////////
      // Modify
      //////////////////////////////////
      let modifyIds = this.modifiedDetails.filter(d => !deleteIds.includes(d));
      let updateWallDetails = wall.filter(
        d => !!d.id && modifyIds.includes(+d.id)
      ).map(d => {
        d.wall_railing = "wall";
        // delete d.area_sqft;
        // delete d.cell_q;
        return d
      })
      let updateRailDetails = railing.filter(
        d => !!d.id && modifyIds.includes(+d.id)
      ).map(d => {
        d.wall_railing = "railing";
        // delete d.area_sqft;
        // delete d.solar_panels;
        // delete d.linear_in_str;
        return d
      })

      updateWallDetails.concat(updateRailDetails).forEach(detail => {
        promises.push(
          this.updateDetail({
            detailId: detail.id,
            data: detail
          })
        )
      });

      return new Promise((resolve, reject) => {
        // Notify success
        Promise.all(promises).then(resp => {
          this.getEstimationSummary(this.estimation.id);
          this.setNotification({
            status: true,
            type: 'success',
            message: 'Estimation successfully updated'
          });
          this.reset();
          this.refreshTables();
          this.saving = false;
          resolve(resp);
        }).catch(err => {
          this.reset();
          this.refreshTables();
          this.saving = false;
          reject(err);
        })
      });
    },
    reset() {
      this.originalIds = [...this.estimation.estimation_details.map(d => d.id)];
      this.modifiedDetails = [];
    },
    refreshTables() {
      this.wallSpreadsheet.setData(this.wallDetails);
      this.wallSpreadsheet.refresh();
      this.wallSpreadsheet.hideColumn(0)
      this.railSpreadsheet.setData(this.railDetails);
      this.railSpreadsheet.refresh();
      this.railSpreadsheet.hideColumn(0)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';
@import '@/styles/flexbox.scss';

.estimation {
  &__info {
    width: 100%;
    margin: 0em;
  }
  &__details {
    width: 100%;
    min-height: 100vh;
    overflow-x: auto;
    overflow-y: visible;
    padding: 0em 1em;
  }
}

.jexcel_container {
  display: block;
  float: left;
}

.capitalize {
  text-transform: capitalize;
}

table .summary-table {
  th:not(:first-child), 
  td:not(:first-child) {
    text-align: right;
  }
}

.subtotal-row {
  border-bottom: 2px solid #ddd;
}
</style>
